import React, { createContext, useContext, useEffect, useState } from "react";
import { authApi, type AuthUser, type SignupInput } from "../lib/auth";

// Client-side session state for the public site. The JWT lives in an httpOnly
// cookie the API sets at login (see lib/auth), so this context can't read it
// directly — it derives the current user from /api/me and exposes the login/
// signup/logout actions the auth pages call.
export type User = AuthUser;

interface UserContextValue {
  user: User | null;
  entitlements: string[];
  canDraw: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const UserContext = createContext<UserContextValue | null>(null);

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within <UserProvider>");
  return ctx;
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [entitlements, setEntitlements] = useState<string[]>([]);
  const [canDraw, setCanDraw] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await authApi.me();
      if (res) {
        setUser(res.user);
        setEntitlements(res.entitlements);
        setCanDraw(res.canDraw);
      } else {
        setUser(null);
        setEntitlements([]);
        setCanDraw(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Populate from the stored token after mount (SSR renders logged-out, so the
  // first client render matches and there's no hydration mismatch).
  useEffect(() => {
    void refresh();
  }, []);

  const login = async (email: string, password: string) => {
    await authApi.login(email, password); // API sets the httpOnly session cookie
    await refresh();
  };

  const signup = async (input: SignupInput) => {
    await authApi.signup(input); // API sets the httpOnly session cookie
    await refresh();
  };

  const logout = async () => {
    await authApi.logout(); // API clears the session cookie
    setUser(null);
    setEntitlements([]);
    setCanDraw(false);
  };

  return (
    <UserContext.Provider value={{ user, entitlements, canDraw, loading, login, signup, logout, refresh }}>
      {children}
    </UserContext.Provider>
  );
};
